import { claimLiveVoiceToolOperation } from '@/lib/live-voice-tool-operations';
import { VOICE_TOOL_DESCRIPTIONS } from '@/lib/live-voice-tool-definitions';
import { tool } from '@openai/agents/realtime';
import { getCurrentChatContextSchema } from '@/lib/voice-call-tool-input';
import {
  resolveRealtimeVoiceDispatchId,
  updateRealtimeVoiceDestination,
} from '@/lib/realtime-voice-destination';
import type { Id } from '@/convex/_generated/dataModel';
import {
  delegationSchema,
  nativeWebSearchSchema,
  quickResearchSchema,
} from '@/components/org-chat/voice-call-tool-input';
import type { RealtimeVoiceToolContext } from '@/components/org-chat/realtime-voice-tool-context';
import {
  dispatchRealtimeVoiceDelegationToOrgChat,
  normalizeRealtimeDelegationMode,
} from '@/lib/realtime-voice-agent';
import {
  formatActiveRealtimeVoiceQuestionnaireContext,
  getActiveRealtimeVoiceQuestionnaire,
} from '@/lib/realtime-voice-questionnaire';

const INACTIVE_CALL_MESSAGE =
  'Internal safety: this voice call is no longer active. Stay silent.';

export function createRealtimeVoiceCoreTools(
  context: RealtimeVoiceToolContext
) {
  const chatContextTool = tool({
    name: 'get_current_chat_context',
    description: VOICE_TOOL_DESCRIPTIONS.get_current_chat_context,
    parameters: getCurrentChatContextSchema,
    strict: true,
    errorFunction: null,
    execute: async (input) => {
      if (!context.isCurrentCall()) return INACTIVE_CALL_MESSAGE;
      const visibleQuestionnaire = getActiveRealtimeVoiceQuestionnaire(
        String(context.orgThreadId)
      );
      if (!context.activeOperationRef.current) {
        return 'Internal safety: get_current_chat_context is only available after the user asks for current chat context out loud. Do not call this tool for opening greetings, visible-question prompts, or status updates.';
      }
      const parsed = getCurrentChatContextSchema.parse(input);
      const dispatchId = resolveRealtimeVoiceDispatchId(
        parsed,
        context.voiceDestinationRef?.current ?? null
      );
      if (parsed.dispatchId?.startsWith('ot_'))
        return context.finishRealtimeToolResult({
          toolName: 'get_current_chat_context',
          result: JSON.stringify({
            status: 'unavailable',
            reason: 'not_a_dispatch',
            threadId: parsed.dispatchId,
          }),
        });
      if (parsed.scope === 'destination' && !dispatchId)
        return context.finishRealtimeToolResult({
          toolName: 'get_current_chat_context',
          result: JSON.stringify({
            status: 'unavailable',
            reason: 'no_verified_destination',
          }),
        });
      if (dispatchId) {
        if (!context.getDispatchContext)
          return context.finishRealtimeToolResult({
            toolName: 'get_current_chat_context',
            result: JSON.stringify({ status: 'unavailable', dispatchId }),
          });
        const result = await context.getDispatchContext({
          orgThreadId: context.orgThreadId,
          sessionId: context.leaseSessionId,
          dispatchId: dispatchId as Id<'threadDispatches'>,
        });
        if (!context.isCurrentCall()) return INACTIVE_CALL_MESSAGE;
        if (
          context.voiceDestinationRef &&
          (parsed.dispatchId || parsed.scope === 'destination')
        )
          context.voiceDestinationRef.current = updateRealtimeVoiceDestination(
            context.voiceDestinationRef.current,
            { dispatchId, status: 'sent' },
            'send'
          );
        return context.finishRealtimeToolResult({
          toolName: 'get_current_chat_context',
          result: result.context,
        });
      }
      if (parsed.scope === 'source' && context.voiceDestinationRef) {
        context.voiceDestinationRef.current = { scope: 'source' };
        if (context.voiceQuestionRef) context.voiceQuestionRef.current = null;
      }
      const result = await context.getRealtimeChatContext({
        orgThreadId: context.orgThreadId,
        sessionId: context.leaseSessionId,
      });
      if (!context.isCurrentCall()) return INACTIVE_CALL_MESSAGE;
      const questionnaireContext =
        formatActiveRealtimeVoiceQuestionnaireContext(visibleQuestionnaire);
      return context.finishRealtimeToolResult({
        toolName: 'get_current_chat_context',
        result: questionnaireContext
          ? `${result.context}\n\n${questionnaireContext}`
          : result.context,
      });
    },
  });

  const delegateTool = tool({
    name: 'delegate_to_design_agent',
    description: VOICE_TOOL_DESCRIPTIONS.delegate_to_design_agent,
    parameters: delegationSchema,
    strict: true,
    errorFunction: null,
    execute: async (input) => {
      if (!context.isCurrentCall()) return INACTIVE_CALL_MESSAGE;
      const parsed = delegationSchema.parse(input);
      if (!claimLiveVoiceToolOperation(context, 'delegation')) {
        return 'Internal safety: this operation already attempted a source-chat handoff or direct chat message. Do not delegate or send it again until the user says a new request. Stay silent unless a brief clarification is needed.';
      }
      const mode = normalizeRealtimeDelegationMode(parsed.mode);
      const receiptId = context.addVoiceDelegationReceipt({
        prompt: parsed.message,
        mode,
      });
      try {
        const result = await dispatchRealtimeVoiceDelegationToOrgChat({
          target: context.orgChatRef.current,
          message: parsed.message,
          mode,
          realtimeVoiceSessionId: context.leaseSessionId,
        });
        if (context.isCurrentCall()) {
          if (context.voiceDestinationRef)
            context.voiceDestinationRef.current = { scope: 'source' };
          if (context.voiceQuestionRef) context.voiceQuestionRef.current = null;
        }
        context.updateVoiceDelegationReceipt(receiptId, {
          status: result.status,
          mode,
        });

        return context.finishRealtimeToolResult({
          toolName: 'delegate_to_design_agent',
          result: result.message,
        });
      } catch (error) {
        context.updateVoiceDelegationReceipt(receiptId, {
          status: 'failed',
          mode,
        });
        throw error;
      }
    },
  });

  const nativeWebSearchTool = tool({
    name: 'web_search',
    description: VOICE_TOOL_DESCRIPTIONS.web_search,
    parameters: nativeWebSearchSchema,
    strict: true,
    errorFunction: null,
    execute: async (input) => {
      if (!context.isCurrentCall()) return INACTIVE_CALL_MESSAGE;
      const parsed = nativeWebSearchSchema.parse(input);
      if (!claimLiveVoiceToolOperation(context, 'web_search')) {
        return 'Internal safety: this delegated operation already attempted the web lookup. Do not call web_search again until the user asks a new question.';
      }

      try {
        const result = await context.runNativeWebSearch({
          orgThreadId: context.orgThreadId,
          sessionId: context.leaseSessionId,
          query: parsed.query,
        });
        if (!context.isCurrentCall()) return INACTIVE_CALL_MESSAGE;
        return context.finishRealtimeToolResult({
          toolName: 'web_search',
          result: result.result,
        });
      } catch (error) {
        if (!context.isCurrentCall()) return INACTIVE_CALL_MESSAGE;
        console.warn('[voice-call] native web search failed', {
          errorName: error instanceof Error ? error.name : 'UnknownError',
        });
        if (!context.isCurrentCall()) return INACTIVE_CALL_MESSAGE;
        return context.finishRealtimeToolResult({
          toolName: 'web_search',
          result:
            'The quick web lookup failed. Tell the user briefly and offer to run quick_research instead.',
        });
      }
    },
  });

  const quickResearchTool = tool({
    name: 'quick_research',
    description: VOICE_TOOL_DESCRIPTIONS.quick_research,
    parameters: quickResearchSchema,
    strict: true,
    errorFunction: null,
    execute: async (input) => {
      if (!context.isCurrentCall()) return INACTIVE_CALL_MESSAGE;
      const parsed = quickResearchSchema.parse(input);
      if (!claimLiveVoiceToolOperation(context, 'quick_research')) {
        return 'Internal safety: this delegated operation already attempted research. Do not call quick_research again until the user asks a new question.';
      }

      const result = await context.runQuickResearch({
        orgThreadId: context.orgThreadId,
        sessionId: context.leaseSessionId,
        query: parsed.query,
      });
      if (!context.isCurrentCall()) return INACTIVE_CALL_MESSAGE;
      return context.finishRealtimeToolResult({
        toolName: 'quick_research',
        result: result.result,
      });
    },
  });

  return {
    chatContextTool,
    delegateTool,
    nativeWebSearchTool,
    quickResearchTool,
  };
}
